import api, { fetchLegalAct, fetchLegalActArticles, fetchDoctrineContent, viewDocumentPdf } from '../../../lib/api/api'
import type { AttachmentType } from '../../../contexts/AttachmentContext'
import { isOfficialSource } from '../../../shared/components/OfficialSourceNotice'
import type { OfficialSource } from '../../../shared/components/OfficialSourceNotice'

const API = '/api/v1'

/** doc_type of the chat sources (server src/vector_db/law_vectors.py).
 * doc_id is the id of the row in its own table; for law_article, of the act. */
export type ViewerDocType =
  | 'law_article'
  | 'case_law_digest'
  | 'doctrine'
  | 'ti_court_decision'
  | 'ch_court_decision'
  | 'firm_document'
  | 'bank_statement'

export interface ArticleItem {
  article_number: string
  content: string
  section_title?: string
  subtitle?: string
}

/** What the document viewer shows: the articles of an act, a text, or a
 * file behind a blob: URL (the caller revokes it when the viewer closes). */
export interface DocumentView {
  articles?: ArticleItem[]
  text?: string
  blobUrl?: string
  contentType?: string
  officialSource?: OfficialSource
  officialUrl?: string
}

type DocId = string | number | null | undefined

const hasId = (docId: DocId): docId is string | number =>
  docId !== null && docId !== undefined && String(docId) !== ''

/** API URL of each kind of source. firm_document is read through
 * viewDocumentPdf, law_article through its act's articles. */
export const getDocumentUrl = (docType: string | undefined, docId: DocId): string | null => {
  if (!hasId(docId)) return null
  const id = encodeURIComponent(String(docId))
  switch (docType) {
    case 'law_article':
      return `${API}/legal-acts/${id}/articles`
    case 'case_law_digest':
      return `${API}/case-law/${id}/download`
    case 'doctrine':
      return `${API}/doctrine/${id}/content`
    case 'ti_court_decision':
      return `${API}/ti-case-law/${id}/text`
    case 'ch_court_decision':
      return `${API}/federal-case-law/${id}/text`
    case 'firm_document':
      return `${API}/documents/${id}/view`
    case 'bank_statement':
      return `${API}/bank-statements/${id}/download`
    default:
      return null
  }
}

const ATTACHMENT_DOC_TYPES: Record<AttachmentType, ViewerDocType> = {
  legal_act: 'law_article',
  case_law_digest: 'case_law_digest',
  doctrine: 'doctrine',
  firm_document: 'firm_document',
}

/** The viewer doc_type of an attachment. */
export const attachmentDocType = (type: AttachmentType): ViewerDocType => ATTACHMENT_DOC_TYPES[type]

// PDFs served with a generic content type (bank statements come as
// octet-stream) would otherwise land in the sandboxed iframe, where the
// browser's PDF viewer does not run.
const isPdfBlob = async (blob: Blob): Promise<boolean> => {
  try {
    return (await blob.slice(0, 5).text()) === '%PDF-'
  } catch {
    return false
  }
}

const loadFile = async (url: string): Promise<DocumentView> => {
  const response = await api.get(url, { responseType: 'blob' })
  const contentType = String(response.headers?.['content-type'] || 'application/octet-stream')
  const blob: Blob = response.data instanceof Blob ? response.data : new Blob([response.data])
  if (!contentType.includes('pdf') && !contentType.startsWith('image/') && await isPdfBlob(blob)) {
    return { blobUrl: URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })), contentType: 'application/pdf' }
  }
  return { blobUrl: URL.createObjectURL(blob), contentType }
}

const loadArticles = async (actId: string): Promise<DocumentView | null> => {
  // The act summary only adds the official-source notice: its failure must
  // not hide the articles.
  const [articles, act] = await Promise.all([
    fetchLegalActArticles(actId),
    fetchLegalAct(actId).catch(() => null),
  ])
  const rows = Array.isArray(articles.data) ? articles.data : []
  if (rows.length === 0) return null
  const view: DocumentView = {
    articles: rows.map((a) => ({
      article_number: a.label || '',
      content: a.content || '',
      section_title: a.section_title || undefined,
      subtitle: a.rubric || undefined,
    })),
  }
  const official = act?.data?.official_source
  if (isOfficialSource(official)) {
    view.officialSource = official
    view.officialUrl = act?.data?.official_url || undefined
  }
  return view
}

/**
 * Load what the viewer shows for a source or an attachment, with the Bearer
 * client (never cookies). Returns null when there is nothing to show (unknown
 * doc_type, no id, empty act or text); HTTP errors are thrown.
 */
export async function loadDocumentView(docType: string | undefined, docId: DocId): Promise<DocumentView | null> {
  const url = getDocumentUrl(docType, docId)
  if (!url || !hasId(docId)) return null
  const id = String(docId)
  switch (docType) {
    case 'law_article':
      return loadArticles(id)
    case 'doctrine': {
      const { data } = await fetchDoctrineContent(id)
      return data?.content ? { text: data.content } : null
    }
    case 'firm_document':
      return { blobUrl: await viewDocumentPdf(id), contentType: 'application/pdf' }
    default:
      return loadFile(url)
  }
}
